import { useState, useMemo, type JSX } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  Save,
  Bookmark,
  AlertTriangle,
  ExternalLink,
} from 'lucide-react';
import clsx from 'clsx';
import { useApp } from '../../hooks/useApp';
import { useParamStore } from '../../hooks/useParamStore';
import { useSettingsConfig } from '../../hooks/useSettingsConfig';
import ConfirmDialog from '../ui/ConfirmDialog';
import ArbiSelect from '../ui/ArbiSelect';
import ArbiToggle from '../ui/ArbiToggle';
import type { Scenario } from '../../hooks/useScenarios';
import {
  FILTER_FIELDS,
  CONSTRAINT_FIELDS,
  GUARDRAIL_FIELDS,
  RISK_FACTOR_FIELDS,
  computeParamDefaults,
  type FilterDef as FieldDef,
} from '../../lib/buyingSessionDefaults';
import { rebalanceProportional } from '../../lib/buyScoreRebalance';
import type { BuyScoreWeightComponent, BuyingSessionRiskPref } from '../../types';
import type { RiskFactorKey } from '../../lib/poConstraints';

// ── Inline input with color coding ──────────────────────────────────────────

interface InlineInputProps {
  value: string;
  unit: string;
  onChange: (value: string) => void;
  defaultValue?: string;
  testId?: string;
}

function InlineInput({
  value,
  unit,
  onChange,
  defaultValue,
  testId,
}: InlineInputProps): JSX.Element {
  const isDefault = value === '' && !!defaultValue;
  const displayVal = isDefault ? defaultValue! : value;
  const isFromDefault = isDefault || (value !== '' && !!defaultValue && value === defaultValue);
  return (
    <div className="flex items-center flex-shrink-0" style={{ width: 88 }}>
      <span className="w-4 flex-shrink-0 text-right pr-1 text-2xs text-ink-muted">
        {unit === '$' ? '$' : ''}
      </span>
      <input
        type="text"
        inputMode="decimal"
        value={displayVal}
        onChange={(e) => onChange(e.target.value)}
        placeholder="—"
        data-testid={testId}
        className={clsx(
          'h-6 text-right text-xs font-mono bg-surface-3 border border-border rounded px-1.5',
          'outline-none focus:border-accent transition-colors',
          isFromDefault ? 'text-accent' : 'text-ink-bright'
        )}
        style={{ width: 56 }}
      />
      <span className="w-4 flex-shrink-0 text-2xs text-ink-muted pl-1">
        {/* Day 95 #DBN-95-2 — render the unit verbatim except '$' (kept as the
            prefix above). Day-94 changed min_velocity '/d'→'/mo' but this
            allowlist only handled '%'/'/d', blanking the '/mo' suffix. */}
        {unit && unit !== '$' ? unit : ''}
      </span>
    </div>
  );
}

// ── Custom dropdown (replaces browser select) ───────────────────────────────

interface DropdownOption {
  value: string;
  label: string;
}

interface ArbiDropdownProps {
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
  emptyMessage?: string;
}

function ArbiDropdown({ value, options, onChange, emptyMessage }: ArbiDropdownProps): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  const selected = options.find((o) => o.value === value) || options[0];
  const hasRealOptions = options.some((o) => o.value !== '');

  return (
    <div className="flex items-center flex-shrink-0" style={{ width: 88 }}>
      {/* Prefix spacer — matches InlineInput alignment */}
      <span className="w-4 flex-shrink-0" />
      <div className="relative" style={{ width: 56 }}>
        <button
          onClick={() => setOpen((v) => !v)}
          className="w-full h-6 flex items-center justify-between text-xs bg-surface-3 border border-border rounded px-1.5
                     text-ink-bright outline-none hover:border-accent transition-colors cursor-pointer"
        >
          <span className="truncate">{selected?.label || '—'}</span>
          <ChevronDown
            size={10}
            className={clsx('flex-shrink-0 ml-0.5 transition-transform', open && 'rotate-180')}
          />
        </button>
        {open && (
          <>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden="true" />
            <div
              className="absolute top-full left-0 z-50 mt-0.5 bg-surface-3 border border-border rounded shadow-lg overflow-hidden"
              style={{ minWidth: 120 }}
            >
              {options.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  className={clsx(
                    'w-full text-left px-2 py-1.5 text-xs hover:bg-surface-4 transition-colors cursor-pointer whitespace-nowrap',
                    opt.value === value ? 'text-accent' : 'text-ink-bright'
                  )}
                >
                  {opt.label}
                </button>
              ))}
              {!hasRealOptions && (
                <div className="px-2 py-1.5 text-xs text-ink-muted italic whitespace-nowrap">
                  {emptyMessage || 'No sheets available'}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {/* Suffix spacer — matches InlineInput alignment */}
      <span className="w-4 flex-shrink-0" />
    </div>
  );
}

// ── Weight toggle row — stable height ───────────────────────────────────────

interface WeightRowProps {
  item: BuyScoreWeightComponent;
  enabled: boolean;
  weight: string;
  onToggle: () => void;
  onWeightChange: (val: string) => void;
  /** Day 91a P2B — fires on blur or Enter; parent triggers proportional
   *  auto-rebalance of remaining is_available=true enabled weights per
   *  R-Lock-6 + R-Lock-7. onChange continues to update display state
   *  per keystroke; onCommit is the rebalance trigger. */
  onCommit?: () => void;
}

function WeightRow({
  item,
  enabled,
  weight,
  onToggle,
  onWeightChange,
  onCommit,
}: WeightRowProps): JSX.Element {
  return (
    <div className="flex items-center px-3 py-1.5 hover:bg-surface-3 transition-colors gap-2">
      {/* Toggle */}
      <button
        onClick={onToggle}
        role="switch"
        aria-checked={enabled}
        aria-label={`Toggle ${item.label}`}
        className={clsx(
          'w-7 h-4 rounded-full flex-shrink-0 flex items-center transition-colors cursor-pointer',
          enabled ? 'bg-accent justify-end' : 'bg-surface-5 justify-start'
        )}
      >
        <span
          className={clsx(
            'w-3 h-3 rounded-full mx-0.5 transition-colors',
            enabled ? 'bg-white' : 'bg-ink-muted'
          )}
        />
      </button>

      {/* Label + penalty icon */}
      <span
        className={clsx(
          'flex-1 text-xs truncate flex items-center gap-1',
          enabled ? 'text-ink-primary' : 'text-ink-muted'
        )}
      >
        {item.label}
        {item.penalty && (
          <AlertTriangle
            size={10}
            className="text-orange-400 flex-shrink-0"
            aria-label="Penalty — subtracts from score"
          />
        )}
      </span>

      {/* Weight input — always reserve space, invisible when disabled */}
      <div className={clsx('flex items-center flex-shrink-0', !enabled && 'invisible')}>
        <input
          type="text"
          inputMode="decimal"
          value={weight}
          onChange={(e) => onWeightChange(e.target.value)}
          onBlur={onCommit}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && onCommit) {
              (e.currentTarget as HTMLInputElement).blur();
            }
          }}
          placeholder="0"
          tabIndex={enabled ? 0 : -1}
          aria-label={`${item.label} weight percentage`}
          className="w-10 h-6 text-right text-xs font-mono bg-surface-3 border border-border rounded px-1
                     text-ink-bright outline-none focus:border-accent transition-colors"
        />
        <span className="text-2xs text-ink-muted ml-0.5 w-3 flex-shrink-0">%</span>
      </div>
    </div>
  );
}

// ── Section header ──────────────────────────────────────────────────────────

interface SectionHeaderProps {
  label: string;
  expanded: boolean;
  onToggle: () => void;
  count: number;
}

function SectionHeader({ label, expanded, onToggle, count }: SectionHeaderProps): JSX.Element {
  return (
    <div>
      <button
        onClick={onToggle}
        className="flex items-center w-full px-3 py-2 text-2xs font-semibold uppercase tracking-wider
                   text-ink-muted bg-surface-2 hover:bg-surface-3 transition-colors cursor-pointer select-none border-b border-border-subtle"
      >
        {expanded ? (
          <ChevronDown size={12} className="mr-1.5 flex-shrink-0" />
        ) : (
          <ChevronUp size={12} className="mr-1.5 flex-shrink-0" />
        )}
        <span className="flex-1 text-left">{label}</span>
        {count > 0 && (
          <span className="ml-1 px-1.5 py-0 rounded-full bg-accent/20 text-accent text-2xs font-medium">
            {count}
          </span>
        )}
      </button>
    </div>
  );
}

// ── ParamsSidebar ───────────────────────────────────────────────────────────

interface WeightState {
  enabled: boolean;
  weight: string;
}

type WeightAction = 'toggle' | 'weight';

interface ConfirmActionPayload {
  label: string;
  message: string;
  action: () => void;
}

interface ParamsSidebarProps {
  open: boolean;
  onToggle: () => void;
  filters: Record<string, string | undefined>;
  weights: Record<string, WeightState | undefined>;
  constraints: Record<string, string | undefined>;
  onFiltersChange: (key: string, value: string) => void;
  onWeightsChange: (key: string, action: WeightAction, value?: string) => void;
  onConstraintsChange: (key: string, value: string) => void;
  // Day 98.1 §10/§11 — optional so this commit is tsc-clean before the
  // BuyingSession wiring lands (Phases 3-4); the existing call site compiles
  // unchanged. guardrails mirror constraints; risk is the restock-like config.
  guardrails?: Record<string, string | undefined>;
  onGuardrailsChange?: (key: string, value: string) => void;
  risk?: BuyingSessionRiskPref;
  onRiskFactorToggle?: (key: RiskFactorKey, enabled: boolean) => void;
  onRiskThresholdChange?: (which: 'velocity_floor' | 'competition_ceiling', value: string) => void;
  onResetFilters: () => void;
  onResetWeights: () => void;
  onResetAll: () => void;
  onSavePreset: () => void;
  onSaveDefaults: () => void;
  // ── Day 54 Phase 4 — scenario picker ──
  scenarios: Scenario[];
  /** Day 80 P5.5 F47 — non-null when useScenarios fetch failed. When set,
   *  picker renders disabled with "Scenarios unavailable" placeholder. */
  scenariosError?: string | null;
  activeScenarioId: string | null;
  onApplyScenario: (scenarioId: string | null) => void;
  // ── Day 58 — tier mode (volume/tiered pricing) ──
  /** Current tier-mode configuration. Persisted in ui_state.tier_mode. */
  tierMode: { enabled: boolean; import_id: string | null };
  /** Patch a subset of tier-mode keys. Caller merges with existing tiers
   *  array from ui_state. */
  onTierModeChange: (patch: { enabled?: boolean; import_id?: string | null }) => void;
  /** Open the TierConfigModal — only enabled when tierMode.enabled is true
   *  AND a sheet is selected. */
  onOpenTierConfig: () => void;
  /** Completed Algopix imports for the active distributor. Empty array =
   *  empty-state notice in the sheet selector. */
  algopixImports: Array<{ id: string; file_name: string; imported_at: string | null }>;
  /** Active fulfillment-path tab ('all' = cross-path view). Tier mode disables when 'all'. */
  selectedPath: string;
}

export default function ParamsSidebar({
  open,
  onToggle,
  filters,
  weights,
  constraints,
  onFiltersChange,
  onWeightsChange,
  onConstraintsChange,
  guardrails,
  onGuardrailsChange,
  risk,
  onRiskFactorToggle,
  onRiskThresholdChange,
  onResetFilters,
  onResetWeights,
  onResetAll,
  onSavePreset,
  onSaveDefaults,
  scenarios,
  scenariosError = null,
  activeScenarioId,
  onApplyScenario,
  tierMode,
  onTierModeChange,
  onOpenTierConfig,
  algopixImports,
  selectedPath,
}: ParamsSidebarProps): JSX.Element {
  const { confirmResets, activeDistributor, buyingSessionPrefs } = useApp();
  const paramStore = useParamStore();
  // Day 91a.5 P2B — Surface 1 bswc graduation per R-Lock-1 + G2 INDEPENDENT
  // determination. useSettingsConfig 5th parallel loader (Day 91a.5 P2A
  // `a5f7994`) supplies bswc rows; useParamStore stays mounted independently
  // for params table consumers (resolver / drift detector / etc.). Two hooks
  // coexist at ParamsSidebar component scope.
  const { buyScoreWeightComponents } = useSettingsConfig();
  // bswcActive = rows with is_available=true per R-Lock-7. Memoized to keep
  // referential stability across renders (visibleWeights + weightTotal use it
  // as a dep; without memo, every render would invalidate downstream useMemos).
  const bswcActive = useMemo<BuyScoreWeightComponent[]>(
    () => buyScoreWeightComponents.filter((r) => r.is_available),
    [buyScoreWeightComponents]
  );

  // Day 55 — derive visible+ordered filter/weight lists from per-user prefs.
  // NULL prefs → fall back to source order from FILTER_FIELDS / bswcActive
  // (Day 91a.5 P2B graduated weights source from inline WEIGHT_COMPONENTS to
  // bswc-driven loader). Stale keys (saved in prefs but no longer in defs)
  // are silently dropped.
  const visibleFilters = useMemo<FieldDef[]>(() => {
    // Day 95 H.4 — available:false filters (min_bsr until Keepa) are hidden in
    // BOTH paths so a stale pref entry can't re-show one. #DBN-95-1: filters are
    // NOT bswc-gated, so this FilterDef-side flag is the correct hide lever.
    if (!buyingSessionPrefs?.filters?.length)
      return FILTER_FIELDS.filter((f) => f.available !== false);
    return buyingSessionPrefs.filters
      .filter((f) => f.visible)
      .sort((a, b) => a.order - b.order)
      .map((f) => FILTER_FIELDS.find((field) => field.key === f.key))
      .filter((f): f is FieldDef => f !== undefined)
      .filter((f) => f.available !== false);
  }, [buyingSessionPrefs]);

  const visibleWeights = useMemo<BuyScoreWeightComponent[]>(() => {
    if (!buyingSessionPrefs?.weights?.length) return bswcActive;
    return buyingSessionPrefs.weights
      .filter((w) => w.visible)
      .sort((a, b) => a.order - b.order)
      .map((w) => bswcActive.find((wd) => wd.key === w.key))
      .filter((w): w is BuyScoreWeightComponent => w !== undefined);
  }, [buyingSessionPrefs, bswcActive]);

  // Day 73 P4.1 β-extraction — derivation lifted to shared helper so the
  // BuyingSession-scope drift detector can call it with the same logic.
  const paramDefaults = useMemo<Record<string, string>>(
    () => computeParamDefaults(paramStore, activeDistributor?.id ?? null, buyingSessionPrefs),
    [paramStore, activeDistributor, buyingSessionPrefs]
  );

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    filters: true,
    weights: true,
    constraints: true,
    guardrails: true,
    risk: true,
  });

  // Confirmation dialog state
  const [confirmAction, setConfirmAction] = useState<ConfirmActionPayload | null>(null);

  const toggleSection = (key: string): void => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // Wrap reset actions with optional confirmation
  const handleReset = (label: string, message: string, action: () => void): void => {
    if (confirmResets) {
      setConfirmAction({ label, message, action });
    } else {
      action();
    }
  };

  // Count active filters — counts only visible-via-prefs entries.
  const activeFilterCount = useMemo(
    () =>
      visibleFilters.filter((f) => filters[f.key] !== undefined && filters[f.key] !== '').length,
    [filters, visibleFilters]
  );

  // Count active weights — counts only visible-via-prefs entries.
  const activeWeightCount = useMemo(
    () => visibleWeights.filter((w) => weights[w.key]?.enabled).length,
    [weights, visibleWeights]
  );

  // Count active constraints
  const activeConstraintCount = useMemo(
    () =>
      CONSTRAINT_FIELDS.filter((c) => constraints[c.key] !== undefined && constraints[c.key] !== '')
        .length,
    [constraints]
  );

  // Day 98.1 §10 — active guardrails (mirrors activeConstraintCount).
  const activeGuardrailCount = useMemo(
    () =>
      GUARDRAIL_FIELDS.filter(
        (g) => guardrails?.[g.key] !== undefined && guardrails?.[g.key] !== ''
      ).length,
    [guardrails]
  );

  // Day 98.1 §11 — enabled risk factors (mirrors activeWeightCount's enabled tally).
  const activeRiskCount = useMemo(
    () => RISK_FACTOR_FIELDS.filter((f) => risk?.factors?.[f.key]?.enabled).length,
    [risk]
  );

  // Weight total — always computed
  const weightTotal = useMemo<number>(() => {
    return bswcActive.reduce((sum, w) => {
      if (!weights[w.key]?.enabled) return sum;
      const val = parseFloat(weights[w.key]?.weight ?? '') || 0;
      return sum + val;
    }, 0);
  }, [weights, bswcActive]);

  // ── Day 91a P2B — proportional auto-rebalance on weight blur ────────────────
  // Fires when user finishes editing a weight (onBlur or Enter). Brings the
  // total back to 100 by redistributing (100 − currentTotal) across the
  // remaining enabled is_available=true weights proportionally per R-Lock-6 +
  // R-Lock-7. Pure math lives in src/lib/buyScoreRebalance.ts (Day 89 #DBN-λ
  // pure-function reclassify candidate per build prompt §6 P3 Block A).
  //
  // No-op when total already equals 100 (within float tolerance) or when no
  // other eligible weights exist. Enhances pre-existing visual sum indicator
  // (L406-412 + L520-568 unchanged) per #DBN-Phase-0-1 reframe — NOT
  // greenfield. Day 91a.5 P2B graduated visibleWeights / weightTotal off
  // inline WEIGHT_COMPONENTS source to bswc-driven loader per R-Lock-14;
  // handleWeightBlur consumes bswc-sourced visibleWeights transparently
  // (functional parity per R-Lock-14 risk class 🟢 substrate-consumer).
  const handleWeightBlur = (blurredKey: string): void => {
    // Compute current total of enabled weights
    const total = visibleWeights
      .filter((w) => weights[w.key]?.enabled)
      .reduce((sum, w) => sum + (parseFloat(weights[w.key]?.weight ?? '') || 0), 0);

    // Float tolerance: skip rebalance when already at 100 within rounding
    if (Math.abs(total - 100) < 0.01) return;

    const delta = 100 - total;
    const eligibleKeys = visibleWeights
      .filter((w) => w.key !== blurredKey && weights[w.key]?.enabled)
      .map((w) => w.key);

    if (eligibleKeys.length === 0) return; // single-weight case — caller can't rebalance

    const currentValues: Record<string, number> = {};
    eligibleKeys.forEach((k) => {
      currentValues[k] = parseFloat(weights[k]?.weight ?? '') || 0;
    });

    const redistributed = rebalanceProportional(currentValues, delta);
    Object.entries(redistributed).forEach(([k, v]) => {
      // Round to 2 decimal places for display; engine consumes weights JSONB
      // verbatim per Day 68 P3.1 (no rounding gate downstream).
      onWeightsChange(k, 'weight', String(Math.round(v * 100) / 100));
    });
  };

  // ── Collapsed state ──────────────────────────────────────
  if (!open) {
    return (
      <button
        onClick={onToggle}
        className="w-6 flex-shrink-0 bg-surface-1 border-r border-border flex items-center justify-center
                   hover:bg-surface-3 transition-colors cursor-pointer"
        title="Show session params"
        aria-label="Show session params"
      >
        <ChevronRight size={12} className="text-ink-muted" />
      </button>
    );
  }

  return (
    <div className="w-64 flex-shrink-0 bg-surface-1 border-r border-border flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border-subtle">
        <span className="text-xs font-semibold text-ink-bright">Session Params</span>
        <div className="flex items-center gap-0.5">
          <button
            onClick={() => window.open('/settings#params-defaults', '_blank')}
            className="p-1 rounded text-ink-muted hover:text-ink-primary hover:bg-surface-4 transition-colors cursor-pointer"
            title="Open in Settings"
            aria-label="Open params in Settings"
          >
            <ExternalLink size={12} />
          </button>
          <button
            onClick={onToggle}
            className="p-1 rounded text-ink-muted hover:text-ink-primary hover:bg-surface-4 transition-colors cursor-pointer"
            title="Hide params"
            aria-label="Hide params"
          >
            <ChevronLeft size={12} />
          </button>
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden pr-2.5">
        {/* ── SCENARIO PICKER (Day 54 Phase 4; Day 76 P5.1 Phase 8C
            prominence bump per Day 55+ Layer 2 audit) ────────── */}
        <div className="px-3 py-2 border-t border-b border-border bg-surface-2">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-2xs uppercase tracking-wider text-ink-muted">Scenario</span>
          </div>
          <ArbiSelect<string>
            value={scenariosError ? '' : (activeScenarioId ?? '')}
            options={[
              { value: '', label: 'Custom (none)' },
              ...scenarios.map((s) => ({
                value: s.id,
                label: s.distributor_code ? `${s.name} · ${s.distributor_code}` : s.name,
              })),
            ]}
            onChange={(v) => {
              const scenarioId = v === '' ? null : v;
              if (scenarioId === null) {
                // Day 91b F59 — non-destructive deselect. handleApplyScenario
                // at BuyingSession.tsx L2783-2787 early-returns with
                // setActiveScenarioId(null), leaving filters/weights/
                // constraints alone. Gate bypassed per R-Lock-7.
                onApplyScenario(null);
                return;
              }
              const scenario = scenarios.find((s) => s.id === scenarioId);
              handleReset(
                'Apply scenario',
                `Apply "${scenario?.name ?? 'scenario'}"? Current filters, weights, and constraints will be replaced.`,
                () => onApplyScenario(scenarioId)
              );
            }}
            placeholder={
              scenariosError
                ? 'Scenarios unavailable'
                : scenarios.length === 0
                  ? 'No saved scenarios'
                  : 'Pick scenario'
            }
            size="xs"
            disabled={!!scenariosError || scenarios.length === 0}
            className="w-full"
          />
        </div>

        {/* ── SECTION 1: FILTERS ─────────────────────────────── */}
        <SectionHeader
          label="Filters"
          expanded={!!expandedSections.filters}
          onToggle={() => toggleSection('filters')}
          count={activeFilterCount}
        />
        {expandedSections.filters && (
          <div className="border-b border-border-subtle">
            {visibleFilters.map((field) => (
              <div key={field.key}>
                <div className="flex items-center justify-between px-3 py-1.5 hover:bg-surface-3 transition-colors">
                  <span className="text-xs text-ink-secondary truncate mr-2">{field.label}</span>
                  <InlineInput
                    value={filters[field.key] ?? ''}
                    unit={field.unit}
                    onChange={(val) => onFiltersChange(field.key, val)}
                    defaultValue={paramDefaults[field.key]}
                    testId={`filter-${field.key}`}
                  />
                </div>
                {/* Day 95 H.2 — muted honesty-affordance line (Tier-Mode-notice
                    pattern). Always-visible beats hover for an honesty hint. */}
                {field.hint && (
                  <p
                    className="text-2xs text-ink-muted px-3 pb-1.5"
                    data-testid={`filter-hint-${field.key}`}
                  >
                    {field.hint}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ── SECTION 2: BUY SCORE WEIGHTS ───────────────────── */}
        <SectionHeader
          label="Buy Score Weights"
          expanded={!!expandedSections.weights}
          onToggle={() => toggleSection('weights')}
          count={activeWeightCount}
        />
        {expandedSections.weights && (
          <div className="border-b border-border-subtle">
            {/* Weight total bar — always visible */}
            <div className="px-3 py-1.5 border-b border-border-subtle bg-surface-2/50">
              <div className="flex items-center justify-between">
                <span className="text-2xs text-ink-muted">Weight Total</span>
                <span
                  className={clsx(
                    'text-xs font-mono font-medium',
                    weightTotal === 0
                      ? 'text-ink-muted'
                      : weightTotal === 100
                        ? 'text-success'
                        : weightTotal > 100
                          ? 'text-danger'
                          : 'text-warning'
                  )}
                >
                  {weightTotal}%
                </span>
              </div>
              {/* Progress bar */}
              <div className="mt-1 h-1 rounded-full bg-surface-4 overflow-hidden">
                <div
                  className={clsx(
                    'h-full rounded-full transition-all',
                    weightTotal === 0
                      ? 'bg-surface-5'
                      : weightTotal === 100
                        ? 'bg-success'
                        : weightTotal > 100
                          ? 'bg-danger'
                          : 'bg-warning'
                  )}
                  style={{ width: `${Math.min(weightTotal, 100)}%` }}
                />
              </div>
              {weightTotal > 0 && weightTotal !== 100 && (
                <p
                  className={clsx(
                    'text-2xs mt-0.5',
                    weightTotal > 100 ? 'text-danger' : 'text-warning'
                  )}
                >
                  {weightTotal > 100
                    ? `${weightTotal - 100}% over — reduce weights to 100%`
                    : `${100 - weightTotal}% remaining`}
                </p>
              )}
            </div>
            {visibleWeights.map((item) => (
              <WeightRow
                key={item.key}
                item={item}
                enabled={weights[item.key]?.enabled || false}
                weight={weights[item.key]?.weight ?? ''}
                onToggle={() => onWeightsChange(item.key, 'toggle')}
                onWeightChange={(val) => onWeightsChange(item.key, 'weight', val)}
                onCommit={() => handleWeightBlur(item.key)}
              />
            ))}
          </div>
        )}

        {/* ── SECTION 3: BUYING CONSTRAINTS ──────────────────── */}
        <SectionHeader
          label="Buying Constraints"
          expanded={!!expandedSections.constraints}
          onToggle={() => toggleSection('constraints')}
          count={activeConstraintCount}
        />
        {expandedSections.constraints && (
          <div className="border-b border-border-subtle">
            {CONSTRAINT_FIELDS.map((field) => (
              <div
                key={field.key}
                className="flex items-center justify-between px-3 py-1.5 hover:bg-surface-3 transition-colors"
              >
                <span className="text-xs text-ink-secondary truncate mr-2">{field.label}</span>
                {field.kind === 'boolean' ? (
                  <ArbiToggle
                    label={field.label}
                    checked={constraints[field.key] === 'true'}
                    onChange={(checked) => onConstraintsChange(field.key, checked ? 'true' : '')}
                  />
                ) : (
                  <InlineInput
                    value={constraints[field.key] ?? ''}
                    unit={field.unit}
                    onChange={(val) => onConstraintsChange(field.key, val)}
                    defaultValue={paramDefaults[field.key]}
                    testId={`constraint-${field.key}`}
                  />
                )}
              </div>
            ))}
            {/* Day 58 — Tier Mode group. Replaces the vestigial
                volume_pricing_ref dropdown that was a placeholder for this
                feature. Single-channel only — toggle disables when
                selectedPath='all' (Architecture Rule 16 grid budget).
                When enabled + sheet picked, the grid filters to that
                supplier sheet and gains T{N} ROI%/PPU columns. */}
            <div className="flex items-center justify-between px-3 py-1.5 hover:bg-surface-3 transition-colors">
              <span
                className={clsx(
                  'text-xs truncate mr-2',
                  selectedPath === 'all' ? 'text-ink-muted' : 'text-ink-secondary'
                )}
              >
                Tier Mode
              </span>
              <ArbiToggle
                label="Tier Mode"
                checked={!!tierMode.enabled}
                onChange={(checked) => onTierModeChange({ enabled: checked })}
                disabled={selectedPath === 'all'}
              />
            </div>
            {selectedPath === 'all' && (
              <p
                className="text-2xs text-ink-muted px-3 pb-1.5"
                data-testid="tier-mode-disabled-notice"
              >
                Tier analysis requires a single channel — switch from All Channels to enable.
              </p>
            )}
            {tierMode.enabled && selectedPath !== 'all' && (
              <>
                <div
                  className="flex items-center justify-between px-3 py-1.5 hover:bg-surface-3 transition-colors"
                  data-testid="tier-supplier-sheet-row"
                >
                  <span className="text-xs text-ink-secondary truncate mr-2">Supplier Sheet</span>
                  <ArbiDropdown
                    value={tierMode.import_id ?? ''}
                    options={
                      algopixImports.length === 0
                        ? [{ value: '', label: 'No completed sheets' }]
                        : [
                            { value: '', label: 'Select sheet…' },
                            ...algopixImports.map((imp) => ({
                              value: imp.id,
                              label: imp.file_name,
                            })),
                          ]
                    }
                    onChange={(val) =>
                      onTierModeChange({ import_id: val === '' ? null : (val as string) })
                    }
                  />
                </div>
                {tierMode.import_id && (
                  <div className="px-3 py-1.5">
                    <button
                      type="button"
                      onClick={onOpenTierConfig}
                      data-testid="open-tier-config"
                      className="arbi-btn-ghost text-2xs w-full py-1.5"
                    >
                      Configure Tiers
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── SECTION 4: GUARDRAILS (§10 advisory) ───────────── */}
        <SectionHeader
          label="Guardrails"
          expanded={!!expandedSections.guardrails}
          onToggle={() => toggleSection('guardrails')}
          count={activeGuardrailCount}
        />
        {expandedSections.guardrails && (
          <div className="border-b border-border-subtle">
            {GUARDRAIL_FIELDS.map((field) => (
              <div
                key={field.key}
                className="flex items-center justify-between px-3 py-1.5 hover:bg-surface-3 transition-colors"
              >
                <span className="text-xs text-ink-secondary truncate mr-2">{field.label}</span>
                <InlineInput
                  value={guardrails?.[field.key] ?? ''}
                  unit={field.unit}
                  onChange={(val) => onGuardrailsChange?.(field.key, val)}
                  defaultValue={paramDefaults[field.key]}
                  testId={`guardrail-${field.key}`}
                />
              </div>
            ))}
          </div>
        )}

        {/* ── SECTION 5: RISK FACTORS (§11) ──────────────────── */}
        <SectionHeader
          label="Risk Factors"
          expanded={!!expandedSections.risk}
          onToggle={() => toggleSection('risk')}
          count={activeRiskCount}
        />
        {expandedSections.risk && (
          <div className="border-b border-border-subtle">
            {RISK_FACTOR_FIELDS.map((field) => {
              const thr = field.threshold != null ? risk?.thresholds?.[field.threshold] : undefined;
              return (
                <div key={field.key} className="px-3 py-1.5 hover:bg-surface-3 transition-colors">
                  <div className="flex items-center justify-between">
                    <span
                      className={clsx(
                        'text-xs truncate mr-2',
                        field.available ? 'text-ink-secondary' : 'text-ink-muted'
                      )}
                    >
                      {field.label}
                    </span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {field.available && field.threshold && (
                        <InlineInput
                          value={thr != null ? String(thr) : ''}
                          unit={field.threshold === 'velocity_floor' ? '/mo' : ''}
                          onChange={(val) => onRiskThresholdChange?.(field.threshold!, val)}
                          testId={`risk-threshold-${field.threshold}`}
                        />
                      )}
                      <ArbiToggle
                        label={field.label}
                        checked={risk?.factors?.[field.key]?.enabled ?? false}
                        disabled={!field.available}
                        onChange={(checked) => onRiskFactorToggle?.(field.key, checked)}
                      />
                    </div>
                  </div>
                  {!field.available && field.hint && (
                    <p
                      className="text-2xs text-ink-muted pt-1"
                      data-testid={`risk-hint-${field.key}`}
                    >
                      {field.hint}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Bottom action bar ──────────────────────────────── */}
      <div className="flex-shrink-0 border-t border-border-subtle bg-surface-2 px-3 py-2 space-y-2">
        {/* Reset buttons row */}
        <div className="flex items-center gap-1">
          <button
            onClick={() =>
              handleReset(
                'Reset Filters',
                'This will reset all filter values and cannot be undone.',
                onResetFilters
              )
            }
            className="flex-1 text-2xs text-ink-muted hover:text-ink-primary hover:bg-surface-4
                       rounded px-2 py-1 transition-colors cursor-pointer text-center"
          >
            <RotateCcw size={10} className="inline mr-0.5 mb-px" />
            Filters
          </button>
          <span className="text-border">|</span>
          <button
            onClick={() =>
              handleReset(
                'Reset Weights',
                'This will reset all weight toggles and values and cannot be undone.',
                onResetWeights
              )
            }
            className="flex-1 text-2xs text-ink-muted hover:text-ink-primary hover:bg-surface-4
                       rounded px-2 py-1 transition-colors cursor-pointer text-center"
          >
            <RotateCcw size={10} className="inline mr-0.5 mb-px" />
            Weights
          </button>
          <span className="text-border">|</span>
          <button
            onClick={() =>
              handleReset(
                'Reset All',
                'This will reset all filters, weights, and constraints and cannot be undone.',
                onResetAll
              )
            }
            className="flex-1 text-2xs text-ink-muted hover:text-ink-primary hover:bg-surface-4
                       rounded px-2 py-1 transition-colors cursor-pointer text-center"
          >
            <RotateCcw size={10} className="inline mr-0.5 mb-px" />
            All
          </button>
        </div>

        {/* Save buttons */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={onSavePreset}
            className="flex-1 arbi-btn text-2xs bg-surface-4 text-ink-secondary hover:text-ink-bright
                       hover:bg-surface-5 justify-center py-1"
          >
            <Save size={10} />
            Save Preset
          </button>
          <button
            onClick={onSaveDefaults}
            className="flex-1 arbi-btn text-2xs text-accent hover:text-accent-hover justify-center py-1"
          >
            <Bookmark size={10} />
            My Defaults
          </button>
        </div>
      </div>

      {/* Confirmation dialog */}
      <ConfirmDialog
        open={!!confirmAction}
        title={confirmAction?.label}
        message={confirmAction?.message}
        onConfirm={() => {
          confirmAction?.action();
          setConfirmAction(null);
        }}
        onCancel={() => setConfirmAction(null)}
      />
    </div>
  );
}
